/*
  Casamé welcome offer — controller (17/9).

  The Omnisend form is CUSTOM-TRIGGERED: Omnisend ignores every display rule and cannot show its
  own teaser, so this file owns everything around the form:
    - timed first open for eligible new visitors (not on a mobile landing page reached from an organic
      Google result — Google's intrusive-interstitial signal; the sticky bar carries that page)
    - the teaser tab after a close, click-to-open only
    - every reminder line on the PDP / cart ([data-cwo-open])
    - the subscribed state, from Omnisend's own `omnisendForms` submit event + its filled-at cookie

  Legal guardrail (L112-1-1 / Art. 6a): a computed personal price is rendered ONLY in the
  subscribed state. The public state is offer + condition, never a struck list price.

  Stored on the device: timestamps and the visitor's own code (needed to auto-apply it). No e-mail.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-cwo-root]');
  if (!root || window.CasameWelcomeOffer) return;

  var cfg = {
    formDesktop: root.dataset.formDesktop || '',
    formMobile: root.dataset.formMobile || root.dataset.formDesktop || '',
    percent: parseInt(root.dataset.percent, 10) || 40,
    codeDays: parseInt(root.dataset.codeDays, 10) || 7,
    delayMs: (parseFloat(root.dataset.delaySeconds) || 12) * 1000,
    delayPdpMs: (parseFloat(root.dataset.delayPdpSeconds) || 20) * 1000,
    scrollRatio: (parseFloat(root.dataset.scrollPercent) || 25) / 100,
    modalCooldownDays: parseFloat(root.dataset.modalCooldownDays) || 7,
    teaserCooldownDays: parseFloat(root.dataset.teaserCooldownDays) || 1,
    autoOpen: root.dataset.autoOpen !== 'false',
    sticky: root.dataset.sticky !== 'false',
    googleOrganicGuard: root.dataset.googleOrganicGuard !== 'false',
    isBuyer: root.dataset.isBuyer === 'true',
    pageType: root.dataset.pageType || '',
    claimUrl: root.dataset.claimUrl || '',
    moneyFormat: root.dataset.moneyFormat || '{{amount_with_comma_separator}} €',
  };

  var DAY = 86400000;
  var K = {
    subscribedAt: 'cwo:v1:subscribedAt',
    code: 'cwo:v1:code', // the visitor's own code: needed on-site to auto-apply and display it
    expiresAt: 'cwo:v1:expiresAt',
    notEligible: 'cwo:v1:notEligible',
    modalClosedAt: 'cwo:v1:modalClosedAt',
    teaserClosedAt: 'cwo:v1:teaserClosedAt',
    stickyClosedAt: 'cwo:v1:stickyClosedAt',
    autoShown: 'cwo:v1:autoShown',
    pageviews: 'cwo:v1:pageviews',
  };

  function read(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* private mode: degrade to stateless */ }
  }
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function isMobile() { return window.matchMedia('(max-width: 749px)').matches; }
  function formId() { return isMobile() ? cfg.formMobile : cfg.formDesktop; }
  function now() { return Date.now(); }
  function within(key, days) {
    var t = parseInt(read('localStorage', key), 10);
    return !!t && now() - t < days * DAY;
  }

  // ---- state -------------------------------------------------------------------------------
  function subscribedAt() {
    var t = parseInt(read('localStorage', K.subscribedAt), 10);
    if (t) return t;
    // Returning on a device where the form was filled before this script shipped, or storage wiped.
    var ids = [cfg.formDesktop, cfg.formMobile];
    for (var i = 0; i < ids.length; i++) {
      if (!ids[i]) continue;
      var c = cookie('omnisend-form-' + ids[i] + '-filled-at');
      if (c) {
        var parsed = Date.parse(c) || parseInt(c, 10);
        if (parsed) { write('localStorage', K.subscribedAt, String(parsed)); return parsed; }
      }
    }
    return 0;
  }

  // 'buyer' | 'expired' | 'subscribed' | 'public'
  function expiry() {
    var e = Date.parse(read('localStorage', K.expiresAt) || '');
    return e || (subscribedAt() + cfg.codeDays * DAY);
  }
  function state() {
    if (cfg.isBuyer || read('localStorage', K.notEligible) === '1') return 'buyer';
    if (!subscribedAt()) return 'public';
    return now() < expiry() ? 'subscribed' : 'expired';
  }
  // « jusqu’au 24 septembre » — a calm date, never a day count; « jusqu’à ce soir » only when it is true.
  function untilText() {
    var e = new Date(expiry());
    var today = new Date();
    if (e.toDateString() === today.toDateString()) return 'jusqu’à ce soir';
    var d = e.getDate();
    return 'jusqu’au ' + (d === 1 ? '1er' : d) + ' ' + e.toLocaleDateString('fr-FR', { month: 'long' });
  }

  // ---- the visitor's own code -------------------------------------------------------------
  // Omnisend cannot show, name or auto-apply a unique code, so our worker mints it (one per e-mail,
  // first order only) and this applies it to the cart: nothing to copy, nothing to type.
  function applyCode(code) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    })
      // /cart/update.js does not reliably echo discount_codes — read the cart itself for Shopify's verdict
      .then(function () { return fetch('/cart.js', { credentials: 'same-origin' }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Single-use code: once it has been spent (or removed in Shopify), Shopify says "not applicable"
        // on a cart that has items. Then every "valid" line goes quiet on this device.
        var mine = (cart.discount_codes || []).filter(function (c) { return c.code === code; })[0];
        if (cart.item_count > 0 && mine && mine.applicable === false) write('localStorage', K.notEligible, '1');
      })
      .catch(function () {})
      .then(function () {
        return fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' });
      })
      .catch(function () {})
      .then(function () { document.dispatchEvent(new CustomEvent('cart:refresh')); });
  }
  // The Omnisend success / already-subscribed step carries a boxed placeholder line ("Création de
  // votre code…"). While the worker answers it pulses, so the short wait reads as intended; then the
  // real code replaces it. `text === null` = start the pulse only.
  function showInForm(text, note) {
    if (!document.getElementById('cwo-form-style')) {
      var st = document.createElement('style');
      st.id = 'cwo-form-style';
      st.textContent =
        '@keyframes cwo-pulse{0%,100%{opacity:1}50%{opacity:.35}}' +
        '[data-cwo-codeline="wait"]{animation:cwo-pulse 1.1s ease-in-out infinite}' +
        '[data-cwo-codeline="code"]{font-family:ui-monospace,SFMono-Regular,Menlo,monospace!important;letter-spacing:.08em;font-weight:600}' +
        '@media (prefers-reduced-motion:reduce){[data-cwo-codeline="wait"]{animation:none}}';
      document.head.appendChild(st);
    }
    var tries = 0;
    (function look() {
      var ps = document.querySelectorAll('p');
      var hit = false;
      for (var i = 0; i < ps.length; i++) {
        var p = ps[i];
        // The placeholder line ends with an INVISIBLE marker (U+2063), so rewording the popup in Omnisend
        // can never break this again; the two literal phrases cover forms saved before the marker existed.
        if (!p.dataset.cwoCodeline && !/⁣|Création de votre code|Votre code arrive/.test(p.textContent)) continue;
        hit = true;
        if (text === null) { if (!p.dataset.cwoCodeline) p.dataset.cwoCodeline = 'wait'; continue; }
        p.textContent = text;
        p.dataset.cwoCodeline = /^CASAME-/.test(text) ? 'code' : 'note';
        if (note && ps[i + 1]) ps[i + 1].textContent = note;
      }
      if (!hit && ++tries < 24) setTimeout(look, 150);
    })();
  }
  function warmWorker() {
    // Wake the worker while the visitor types their e-mail, so the claim answers faster.
    if (cfg.claimUrl) fetch(cfg.claimUrl.replace(/\/claim$/, '/warm'), { method: 'POST' }).catch(function () {});
  }
  // Everything this browser knows at signup (27/9): ad-platform click/browser ids + visit context. The
  // worker saves it on the Omnisend contact (names = worker's SIGNUP_FIELDS) so later e-mails and orders
  // on another device can use it. To add a platform: one entry in SOURCES + one line in the worker.
  // Sources, in priority order: URL · the cart script's 90-day `_scc_*` mirrors · the platform's cookie.
  function signupFields() {
    var out = {}, now = Date.now(), ttl = 90 * 86400000;
    var q = function (k) { try { return new URLSearchParams(location.search).get(k) || ''; } catch (e) { return ''; } };
    var ls = function (k) { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } };
    var ck = function (name) { var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')); try { return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return ''; } };
    var mirror = function (k) { var seen = Date.parse(ls('_scc_' + k + '_ts')); return isFinite(seen) && now - seen < ttl ? ls('_scc_' + k) : ''; };
    var gcl = function (name) { var p = ck(name).split('.'); return p[0] === 'GCL' && now - Number(p[1]) * 1000 < ttl ? p.slice(2).join('.') : ''; };
    var SOURCES = {
      gclid: function () { return [q('gclid'), q('scc_ref'), mirror('gclid'), gcl('_gcl_aw')]; },
      gbraid: function () { return [q('gbraid'), mirror('gbraid'), gcl('_gcl_gb')]; },
      wbraid: function () { return [q('wbraid'), mirror('wbraid')]; },
      msclkid: function () { return [q('msclkid'), q('scc_mref'), mirror('msclkid'), ck('_uetmsclkid').replace(/^_uet/, '')]; },
      uetvid: function () { return [ck('_uetvid')]; },
      fbclid: function () { return [q('fbclid'), mirror('fbclid'), ck('_fbc').split('.').slice(3).join('.')]; },
      fbc: function () { return [ck('_fbc')]; },
      fbp: function () { return [ck('_fbp')]; },
      epik: function () { return [q('epik'), ck('_epik')]; },
      pin_unauth: function () { return [ck('_pin_unauth')]; },
      ttclid: function () { return [q('ttclid'), ck('ttclid')]; },
      ga_client_id: function () { var g = ck('_ga').split('.'); return [g.length >= 4 ? g[2] + '.' + g[3] : '']; },
      scc_vid: function () { return [ls('_scc_vid')]; },
      first_seen: function () { return [ls('_scc_ft')]; },
      consent: function () { try { var st = window.Shopify.customerPrivacy.marketingAllowed(); return [st === true ? 'shopify:marketing_allowed' : st === false ? 'shopify:marketing_not_allowed' : 'shopify:unavailable']; } catch (e) { return ['shopify:unavailable']; } },
      landing_page: function () { return [ls('_scc_lp'), location.pathname + location.search]; }
    };
    for (var k in SOURCES) {
      var list = SOURCES[k]();
      for (var i = 0; i < list.length; i++) if (list[i] && list[i] !== 'undefined') { out[k] = String(list[i]).slice(0, 1000); break; }
    }
    // UTMs of the first landing (falls back to this page)
    try {
      var lp = new URL(out.landing_page || location.href, location.origin).searchParams;
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (u) { if (lp.get(u)) out[u] = lp.get(u).slice(0, 300); });
    } catch (e) {}
    return out;
  }
  function claim(email) {
    if (!cfg.claimUrl || !email) return;
    showInForm(null);
    fetch(cfg.claimUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({ email: email }, signupFields())),
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status === 'issued' || res.status === 'existing') {
          write('localStorage', K.code, res.code);
          write('localStorage', K.expiresAt, res.expiresAt);
          showInForm(res.code, 'Appliqué automatiquement à votre panier. Valable ' + untilText() + ', sur votre première commande.');
          applyCode(res.code).then(render);
        } else if (res.status === 'not_eligible') {
          write('localStorage', K.notEligible, '1');
          showInForm(
            'Offre réservée à une première commande',
            'Nous avons retrouvé une commande passée avec cette adresse : le code de bienvenue ne peut pas être émis. Merci de votre fidélité.'
          );
        }
        render();
      })
      .catch(function () { showInForm('Votre code arrive par e-mail'); /* the welcome e-mail is the fallback */ });
  }

  var formOpen = false;
  var inlineLineVisible = false; // PDP: the bar takes over only when the line above the title is out of view
  var cartLineVisible = false; // the cart carries its own line; nothing fixed may sit on the checkout button

  // ---- rendering ---------------------------------------------------------------------------
  function money(cents) {
    var amount = (cents / 100).toFixed(2);
    var comma = amount.replace('.', ',');
    return cfg.moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, comma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]+>/g, '');
  }

  function render() {
    var s = state();
    document.documentElement.setAttribute('data-cwo-state', s);

    var reminders = document.querySelectorAll('[data-cwo-reminder]');
    for (var i = 0; i < reminders.length; i++) {
      var el = reminders[i];
      // The code skips compare-at sale items (automated "not on sale" collection), so never promise it there.
      var hidden = s === 'buyer' || s === 'expired' || el.dataset.onSale === 'true';
      if (el.dataset.cwoReminder === 'sticky') {
        hidden = hidden || formOpen || cartLineVisible || inlineLineVisible || within(K.stickyClosedAt, cfg.teaserCooldownDays) || consentPending();
      }
      el.hidden = hidden;
      if (hidden) continue;

      var pub = el.querySelector('[data-cwo-public]');
      var sub = el.querySelector('[data-cwo-subscribed]');
      if (pub) pub.hidden = s !== 'public';
      if (sub) sub.hidden = s !== 'subscribed';

      if (s === 'subscribed') {
        var codeEl = el.querySelector('[data-cwo-code]');
        var code = read('localStorage', K.code);
        if (codeEl) { codeEl.textContent = code || ''; codeEl.hidden = !code; }
        var note = el.querySelector('[data-cwo-codenote]');
        if (note) note.hidden = !code;
        var until = el.querySelector('[data-cwo-until]');
        if (until) until.textContent = untilText();
        var priceEl = el.querySelector('[data-cwo-your-price]');
        var cents = parseInt(el.dataset.priceCents, 10);
        if (priceEl && cents) priceEl.textContent = money(Math.round(cents * (100 - cfg.percent) / 100));
        // cart: the code reduces eligible lines only (compare-at sale items are excluded from it)
        var total = parseInt(el.dataset.cartTotalCents, 10);
        var eligible = parseInt(el.dataset.cartEligibleCents, 10) || 0;
        if (priceEl && total) priceEl.textContent = money(total - Math.round(eligible * cfg.percent / 100));
        if (priceEl && total && !eligible) priceEl.parentNode.hidden = true;
      }
    }
    renderTeaser();
  }

  var teaser = root.querySelector('[data-cwo-teaser]');
  function renderTeaser() {
    if (!teaser) return;
    var show =
      !cfg.sticky &&
      state() === 'public' &&
      !formOpen &&
      !cartLineVisible &&
      cfg.pageType !== 'cart' &&
      !within(K.teaserClosedAt, cfg.teaserCooldownDays) &&
      // Before the first automatic open the teaser stays out of the way — except on mobile, where it
      // IS the first-page surface.
      (read('sessionStorage', K.autoShown) === '1' || within(K.modalClosedAt, 3650) || isMobile() || !cfg.autoOpen);
    teaser.hidden = !show;
  }

  // ---- open / events -----------------------------------------------------------------------
  function open(source) {
    var id = formId();
    if (!id) return false;
    formOpen = true;
    render();
    window.omnisend = window.omnisend || [];
    window.omnisend.push(['openForm', id]);
    document.dispatchEvent(new CustomEvent('cwo:open', { detail: { source: source || 'click' } }));
    return true;
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cwo-open]');
    if (opener) {
      if (state() !== 'public') return; // subscribed line is informational
      e.preventDefault();
      open(opener.dataset.cwoOpen || 'click');
      return;
    }
    if (e.target.closest('[data-cwo-sticky-close]')) {
      e.preventDefault();
      write('localStorage', K.stickyClosedAt, String(now()));
      render();
      return;
    }
    if (e.target.closest('[data-cwo-teaser-close]')) {
      e.preventDefault();
      write('localStorage', K.teaserClosedAt, String(now()));
      renderTeaser();
    }
  });

  window.addEventListener('omnisendForms', function (e) {
    var d = e.detail || {};
    var id = d.form && d.form.id;
    if (id && id !== cfg.formDesktop && id !== cfg.formMobile) return; // another Omnisend form
    if (d.type === 'stepView' || d.type === 'view') warmWorker();
    if (d.type === 'submit') {
      write('localStorage', K.subscribedAt, String(now()));
      var v = d.formValues || {};
      claim(v.emailField || v.email || '');
      render();
    } else if (d.type === 'close') {
      formOpen = false;
      if (state() === 'public') write('localStorage', K.modalClosedAt, String(now()));
      render();
    }
  });

  document.addEventListener('variant:changed', function (e) {
    var v = e.detail && e.detail.variant;
    if (!v) return;
    var els = document.querySelectorAll('[data-cwo-reminder][data-price-cents]');
    for (var i = 0; i < els.length; i++) {
      els[i].dataset.priceCents = v.price;
      els[i].dataset.onSale = v.compare_at_price && v.compare_at_price > v.price ? 'true' : 'false';
    }
    render();
  });

  var cartWatch = 'IntersectionObserver' in window && new IntersectionObserver(function (entries) {
    cartLineVisible = entries.some(function (en) { return en.isIntersecting; });
    render();
  });
  function watchCartLines() {
    if (!cartWatch) return;
    cartWatch.disconnect();
    var lines = document.querySelectorAll('[data-cwo-reminder="cart"]');
    for (var i = 0; i < lines.length; i++) cartWatch.observe(lines[i]);
  }
  var inlineWatch = 'IntersectionObserver' in window && new IntersectionObserver(function (entries) {
    inlineLineVisible = entries.some(function (en) { return en.isIntersecting; });
    render();
  });
  function watchInlineLine() {
    // the product-page line, or the « Code promo » page's own card: while either is on screen the bar stays away
    var lines = document.querySelectorAll('[data-cwo-reminder="above-title"], [data-cwo-reminder="page"]');
    if (!inlineWatch || !lines.length) return;
    inlineLineVisible = true;
    for (var i = 0; i < lines.length; i++) inlineWatch.observe(lines[i]);
  }

  // The cart drawer re-renders its HTML on every cart mutation; re-apply the state to the new nodes.
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      for (var j = 0; j < records[i].addedNodes.length; j++) {
        var n = records[i].addedNodes[j];
        if (n.nodeType === 1 && (n.matches('[data-cwo-reminder]') || n.querySelector('[data-cwo-reminder]'))) {
          render();
          watchCartLines();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // ---- automatic first open ----------------------------------------------------------------
  // eligible AND cookie banner answered AND delay elapsed AND the visitor has scrolled a little.
  // The numbers are hypotheses (vendor data: 6-15 s beats immediate) — tune from the metafield.
  function consentPending() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || !cp.shouldShowBanner || !cp.shouldShowBanner()) return false;
      var c = cp.currentVisitorConsent ? cp.currentVisitorConsent() : {};
      return !c.marketing && !c.analytics; // '' = no decision yet, the banner is still up
    } catch (e) { return false; }
  }
  // Google's mobile interstitial signal only concerns a visitor landing from an ORGANIC Google result.
  function fromGoogleOrganic() {
    if (!/^https?:\/\/([a-z0-9-]+\.)*google\.[a-z.]+\//i.test(document.referrer || '')) return false;
    return !/[?&](gclid|gbraid|wbraid|gad_source|utm_medium=(cpc|paid|ppc))/i.test(location.search);
  }
  function scrolledEnough() {
    var doc = document.documentElement;
    var room = doc.scrollHeight - window.innerHeight;
    return room < 200 || window.scrollY / room >= cfg.scrollRatio;
  }

  function scheduleAutoOpen() {
    var views = (parseInt(read('sessionStorage', K.pageviews), 10) || 0) + 1;
    write('sessionStorage', K.pageviews, String(views));

    if (!cfg.autoOpen || state() !== 'public') return;
    if (cfg.pageType === 'cart' || cfg.pageType === 'customers' || cfg.pageType === 'policy') return;
    if (read('sessionStorage', K.autoShown) === '1') return;
    if (within(K.modalClosedAt, cfg.modalCooldownDays)) return;
    if (cfg.googleOrganicGuard && isMobile() && views < 2 && fromGoogleOrganic()) return; // bar only on that landing page

    // The PDP already shows the line above the title, so the popup is a second nudge there.
    var delay = cfg.pageType === 'product' ? cfg.delayPdpMs : cfg.delayMs;
    var timeUp = false;
    var done = false;

    function attempt() {
      if (done || !timeUp || !scrolledEnough()) return;
      if (state() !== 'public' || formOpen || consentPending()) return;
      if (document.querySelector('cart-drawer[open], .drawer[open], dialog[open]')) return; // never over the cart
      done = true;
      window.removeEventListener('scroll', attempt);
      write('sessionStorage', K.autoShown, '1');
      open('auto');
    }
    function startClock() {
      setTimeout(function () { timeUp = true; attempt(); }, delay);
      window.addEventListener('scroll', attempt, { passive: true });
    }

    if (!consentPending()) return startClock();
    // Never on top of the cookie banner: the clock starts when the visitor has answered it.
    var waited = false;
    document.addEventListener('visitorConsentCollected', function () {
      if (!waited) { waited = true; startClock(); }
    });
    // Still unanswered after 45 s: leave this page alone, the teaser is enough.
  }

  document.addEventListener('visitorConsentCollected', render);

  // ---- other ways in: the footer newsletter band, and plain links ------------------------------
  // The footer band is a native Shopify customer form: it subscribes the visitor itself and reloads the
  // page with a success message. We only remember the address across that reload, then claim the code —
  // same worker, same rules (one per address, first order only).
  var PENDING = 'cwo:v1:pendingEmail';
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.querySelector) return;
    var email = f.querySelector('input[name="contact[email]"]');
    var tags = f.querySelector('input[name="contact[tags]"]');
    if (email && email.value && tags && /newsletter/.test(tags.value)) write('sessionStorage', PENDING, email.value.trim());
  }, true);
  (function claimAfterFooterSignup() {
    var pending = read('sessionStorage', PENDING);
    if (!pending) return;
    var ok = document.querySelector('[id^="Newsletter-success--"]') || /[?&]customer_posted=true/.test(location.search);
    if (!ok) return; // captcha step or error: keep the address for the next page
    try { window.sessionStorage.removeItem(PENDING); } catch (e) {}
    if (state() !== 'public') return;
    write('localStorage', K.subscribedAt, String(now()));
    claim(pending);
  })();
  // Any link ending in #offre-bienvenue (the « Code promo » page, a menu item…) opens the popup.
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href$="#offre-bienvenue"]');
    if (!a || state() !== 'public') return;
    e.preventDefault();
    open('link');
  });

  // ---- the e-mails' copy of the basket (19/9) ------------------------------------------------
  // The abandonment e-mails show the real struck price (« 83,90 € → 50,34 € »). Omnisend knows the
  // contact but not the cart; this page knows the cart and the visitor's code but not the address.
  // So: after every cart change, and on the cart page, send {code, cart totals} to the worker, which
  // owns the code → e-mail link. Fire-and-forget: it never blocks, never throws, never delays a
  // click, and does nothing at all for a visitor without a personal code.
  var BASKET_URL = cfg.claimUrl ? cfg.claimUrl.replace(/\/claim$/, '/basket') : '';
  var TRACK_URL = cfg.claimUrl ? cfg.claimUrl.replace(/\/claim$/, '/track') : '';
  var SENT_BASKET = 'cwo:v1:basketSent'; // the last before:after pair mirrored, this session
  var SENT_TRACK = 'cwo:v1:trackSent';
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|$|\?)/;
  var basketTimer = null;

  function post(url, payload) {
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true, // survives the click that leaves the page
      }).catch(function () {});
    } catch (e) { /* never break a cart action */ }
  }

  function mirrorBasket() {
    if (!BASKET_URL) return;
    // The code lives only in the browser that saw the popup. On any other device Omnisend's own cookie says
    // who this is (it is there whenever a cart e-mail can fire): the worker takes either one (20/9).
    var code = state() === 'subscribed' ? read('localStorage', K.code) : null;
    var who = (document.cookie.match(/(?:^|;\s*)omnisendContactID=([0-9a-f]{24})/) || [])[1] || null;
    if (!code && !who) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) return; // empty cart: nothing to strike through
        var before = cart.original_total_price;
        var after = cart.total_price;
        if (typeof before !== 'number' || typeof after !== 'number' || before <= 0) return;
        // Same numbers as last time (a re-render, a second tab, a no-op update): stay quiet.
        var stamp = before + ':' + after;
        if (read('sessionStorage', SENT_BASKET) === stamp) return;
        write('sessionStorage', SENT_BASKET, stamp);
        post(BASKET_URL, { code: code, contact: who, before: before, after: after, currency: cart.currency || 'EUR' });
      })
      .catch(function () {});
  }
  // One call after a burst: adding three covers fires add + change + drawer refresh in a second.
  function mirrorBasketSoon() {
    if (basketTimer) clearTimeout(basketTimer);
    basketTimer = setTimeout(mirrorBasket, 1500);
  }

  // Every theme mutates the cart through one of these two; wrap both rather than guess which.
  if (BASKET_URL && window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var out = nativeFetch.apply(window, arguments);
      try {
        var u = typeof input === 'string' ? input : (input && input.url) || '';
        if (CART_WRITE.test(u) && out && out.then) out.then(mirrorBasketSoon, function () {});
      } catch (e) {}
      return out;
    };
  }
  if (BASKET_URL && window.XMLHttpRequest) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { if (CART_WRITE.test(String(url || ''))) this.addEventListener('load', mirrorBasketSoon); } catch (e) {}
      return nativeOpen.apply(this, arguments);
    };
  }

  // ---- which welcome track the e-mail sent them down ------------------------------------------
  // E1 offers two doors (le plaid / la housse). The clicked one becomes a tag on the contact, so the
  // rest of the flow can speak about the right product. Once per session, nothing to store.
  function sendTrack() {
    if (!TRACK_URL || state() !== 'subscribed') return;
    var code = read('localStorage', K.code);
    var q = location.search || '';
    if (!code || !/[?&]utm_content=e1-code(&|$)/.test(q)) return;
    var m = q.match(/[?&]utm_term=(plaid|housse)(&|$)/);
    if (!m) return;
    if (read('sessionStorage', SENT_TRACK) === m[1]) return;
    write('sessionStorage', SENT_TRACK, m[1]);
    post(TRACK_URL, { code: code, track: m[1] });
  }

  window.CasameWelcomeOffer = { open: open, state: state, render: render, _keys: K };
  watchInlineLine();
  // Returning in a fresh session: make sure the visitor's code is still on the cart.
  if (state() === 'subscribed' && read('localStorage', K.code) && read('sessionStorage', 'cwo:v1:applied') !== '1') {
    write('sessionStorage', 'cwo:v1:applied', '1');
    applyCode(read('localStorage', K.code)).then(render);
  }
  render();
  watchCartLines();
  scheduleAutoOpen();
  sendTrack();
  // The cart page (and anything on the way to checkout) is where the numbers are freshest, and where
  // an abandonment is about to start: mirror once on load, even without a cart change.
  if (cfg.pageType === 'cart' || /\/(cart|checkout|checkouts|panier)(\/|$)/.test(location.pathname)) mirrorBasketSoon();
})();
